// First-run landing surface, rendered in place of SourcePane until the
// user picks a path. Brutalist phosphor-CRT aesthetic: oversized
// Space-Mono wordmark, scanline texture, ambient 16-step rhythm grid
// that intensifies while a four-bar demo plays through the engine.
//
// All visuals are CSS-only — the rhythm grid driver is `data-playing`
// flipping the keyframes' duration/intensity. No render churn during
// playback (the App polls hap data at 12fps; we don't subscribe here).
//
// Three exits, ranked by ambition:
//   1. Hear the demo + open it in the editor — the inviting path.
//   2. Start blank — for the impatient.
//   3. Browse community — for the curious.
//
// Dismissal is the parent's responsibility; every action prop fires
// the corresponding handler in App.tsx which both does the action AND
// flips `welcomed` to true.

import { useEffect, useState } from 'react';
import type { PlayerPhase } from '../strudel/types';
import type { Song } from '../types';

// Mirrors LessonAccent in src/learn/types.ts. Duplicated here to keep
// WelcomePane independent of the learn module's import graph — the
// home page only needs the colour token, not the lesson catalog.
export type HomeLessonCard = {
  id: string;
  title: string;
  moduleTitle: string;
  accent: 'yellow' | 'emerald' | 'cyan' | 'fuchsia';
  estMinutes: number;
};

export const DEMO_CODE = `setcpm(110 / 4)

stack(
  // beat: kick on quarters, snare on the offs, ticking hats
  s("bd*2, [~ sd]*2, hh*8").bank("RolandTR909"),

  // bass: four notes, swept by a slow LFO
  note("c2 c2 [eb2 g2] f2")
    .s("sawtooth")
    .lpf(sine.range(400, 1800).slow(4))
    .lpq(8)
    .gain(0.7),

  // melody: triangle-wave hook with delay + room
  note("eb5 g5 ~ <bb5 c6>")
    .s("triangle")
    .gain(0.35)
    .delay(0.4)
    .room(0.6),
)
`;

// Display snippet derived from DEMO_CODE — strip the comment lines and
// any blank lines they leave behind, so what the user reads on the card
// is the same code that's playing through the engine. Keeping these in
// sync by hand drifted the first time around (snippet dropped .bank,
// .lpq, .gain, .room) which is exactly the trust-erosion an onboarding
// surface can't afford. Computed once at module load.
const DEMO_CODE_SNIPPET = DEMO_CODE.split('\n')
  .filter((line) => !/^\s*\/\//.test(line))
  .join('\n')
  // Collapse adjacent blank lines left behind by removed comments.
  .replace(/\n{3,}/g, '\n\n')
  .trim();

type Props = {
  phase: PlayerPhase;
  /** True while the welcome pane's own demo is playing. Distinct from
   *  phase==='playing' because the user could trigger playback by
   *  some other path (unlikely on first run, but cheap to be precise). */
  demoPlaying: boolean;
  /** True until the user has completed (or dismissed) the first-run
   *  tour. Swaps the primary CTA to the one-click "Jump in" path. */
  firstRun: boolean;
  /** First-run primary action: hear the demo AND open it in the
   *  editor in one gesture, then start the coachmark tour. */
  onJumpIn: () => void;
  onHearDemo: () => void;
  onStopDemo: () => void;
  onOpenDemoInEditor: () => void;
  onStartBlank: () => void;
  onBrowseCommunity: () => void;
  /** Most-recently-edited library songs, sorted desc by modifiedAt.
   *  Empty array hides the strip. Capped by the caller (App). */
  recentSongs: Song[];
  /** Songs flagged as favourite. Empty array hides the strip. */
  favouriteSongs: Song[];
  /** Curated lesson cards for the Lessons strip. Stable per-build. */
  lessonCards: readonly HomeLessonCard[];
  /** Open a library song in the editor (selects + flips view). */
  onOpenSong: (id: string) => void;
  /** Toggle a song's favourite flag from a strip card (the star
   *  button on each Recent / Favourites card). */
  onToggleFavourite: (id: string) => void;
  /** Open the Learn modal pre-positioned on a specific lesson. */
  onOpenLesson: (lessonId: string) => void;
};

const STEP_COUNT = 16;
// Pre-canned hit map for the bottom-strip rhythm grid. Mirrors the
// kick/snare/hat skeleton of DEMO_CODE so the visual matches what the
// user is hearing — the kick lands on 0 and 8, snare on 4 and 12, hats
// every other step. We don't query the engine for this (would require
// the welcome pane to subscribe to hap data, which is overkill for an
// ambient ornament).
const ROWS: ReadonlyArray<{ id: string; label: string; hits: readonly number[] }> = [
  { id: 'k', label: 'KCK', hits: [0, 8] },
  { id: 's', label: 'SNR', hits: [4, 12] },
  { id: 'h', label: 'HAT', hits: [0, 2, 4, 6, 8, 10, 12, 14] },
];

export function WelcomePane({
  phase,
  demoPlaying,
  firstRun,
  onJumpIn,
  onHearDemo,
  onStopDemo,
  onOpenDemoInEditor,
  onStartBlank,
  onBrowseCommunity,
  recentSongs,
  favouriteSongs,
  lessonCards,
  onOpenSong,
  onToggleFavourite,
  onOpenLesson,
}: Props) {
  const engineBooting = phase === 'loading' || phase === 'idle';
  // Mirrors TransportDeck.playDisabled — strudel.play is only safe once
  // initPlayer has resolved the engine into 'ready'. `phase === 'error'`
  // is implicitly excluded here, so the disabled prop doesn't need to
  // restate it.
  const canPlay =
    phase === 'ready' || phase === 'playing' || phase === 'paused';

  // Ambient cursor blink — pure CSS would do, but we want the cursor
  // to *stop* blinking briefly when the demo first kicks in (a tiny
  // surprise that rewards the click). Toggled via key.
  const [cursorKey, setCursorKey] = useState(0);
  useEffect(() => {
    if (demoPlaying) setCursorKey((n) => n + 1);
  }, [demoPlaying]);

  return (
    <div
      className="welcome-pane relative flex-1 min-h-0 min-w-0 overflow-y-auto bg-zinc-950 text-zinc-200"
      data-playing={demoPlaying ? '' : undefined}
    >
      {/* Background layers: phosphor wash, vignette, scanlines.
          All pointer-events: none so they never eat clicks. */}
      <div className="welcome-bg-phosphor pointer-events-none absolute inset-0" />
      <div className="welcome-bg-scanlines pointer-events-none absolute inset-0" />
      <div className="welcome-bg-vignette pointer-events-none absolute inset-0" />

      <div className="relative mx-auto flex min-h-full w-full max-w-[920px] flex-col gap-8 px-3 py-6 sm:gap-10 sm:px-12 sm:py-14">
        {/* Operator-console stat bar — sets the brutalist tone. */}
        <header className="welcome-reveal flex items-center justify-between text-[10px] uppercase tracking-[0.3em] text-zinc-500" style={{ '--i': 0 } as React.CSSProperties}>
          <span>
            <span className="text-yellow-300/70" aria-hidden="true">●</span>{' '}
            CRT&nbsp;WARM&nbsp;·&nbsp;BUFFER&nbsp;NOMINAL
          </span>
          <span>{enginePhaseLabel(phase)}</span>
        </header>

        {/* Wordmark + cursor + tagline. */}
        <section className="flex flex-col gap-3">
          <h1
            className="welcome-reveal flex items-baseline gap-3 font-display text-[64px] font-bold leading-none tracking-[-0.04em] text-yellow-300 sm:text-[96px]"
            style={{ '--i': 1 } as React.CSSProperties}
          >
            <span className="welcome-wordmark">STRUDEL</span>
            <span
              key={cursorKey}
              aria-hidden="true"
              className="welcome-cursor inline-block h-[0.7em] w-[0.32em] translate-y-[-0.04em] bg-yellow-300"
            />
          </h1>
          <p
            className="welcome-reveal max-w-[42ch] font-body text-[13px] leading-relaxed text-zinc-400"
            style={{ '--i': 2 } as React.CSSProperties}
          >
            Live-code music in the browser. Type a pattern, hear it the
            same beat. Refine it, layer it, rip it apart — the loop never
            stops to wait for you.
          </p>
        </section>

        {/* Hero: code snippet on the left, action stack on the right.
            Stacks vertically below ~640px (sm breakpoint flips). */}
        <section
          className="welcome-reveal grid gap-6 sm:grid-cols-[1fr_minmax(220px,260px)]"
          style={{ '--i': 3 } as React.CSSProperties}
        >
          <pre className="welcome-code relative overflow-hidden rounded-sm border border-zinc-800 bg-zinc-900/60 p-4 font-body text-[12px] leading-[1.55] text-zinc-300 shadow-[inset_0_1px_0_rgba(255,255,255,0.02)]">
            <span className="absolute right-2 top-2 font-display text-[9px] uppercase tracking-[0.25em] text-zinc-600">
              demo.strudel
            </span>
            {renderDemoSnippet()}
          </pre>

          <div className="flex flex-col gap-3">
            {/* First run swaps the primary CTA for the one-gesture
                epiphany path: hear the demo AND land in the editor
                with hot-swap armed, then the coachmark tour begins.
                Returning users keep the hear/stop toggle. */}
            <button
              type="button"
              onClick={
                firstRun ? onJumpIn : demoPlaying ? onStopDemo : onHearDemo
              }
              disabled={!canPlay}
              data-playing={!firstRun && demoPlaying ? '' : undefined}
              data-booting={engineBooting ? '' : undefined}
              className="welcome-cta-primary group relative flex items-center justify-between gap-3 rounded-sm border border-yellow-300/70 bg-yellow-300/10 px-4 py-3 text-left font-display text-[12px] uppercase tracking-[0.18em] text-yellow-200 transition-colors hover:bg-yellow-300/20 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <span className="flex items-center gap-3">
                <span className="welcome-cta-glyph text-[14px]" aria-hidden="true">
                  {!firstRun && demoPlaying ? '■' : '▶'}
                </span>
                <span>
                  {firstRun
                    ? 'Jump in — hear it, then change it'
                    : demoPlaying
                      ? 'Stop demo'
                      : 'Hear the demo'}
                </span>
              </span>
              {engineBooting ? (
                // Booting: replace the static "BOOT" label with a small
                // animated dot so the disabled state reads as "warming
                // up" rather than "broken". The label-as-text under
                // disabled-opacity sat near the WCAG floor; animation
                // gives the eye something to latch onto regardless.
                <span className="flex items-center gap-1.5 text-[9px] tracking-[0.3em] text-yellow-200/80">
                  <span className="welcome-boot-dot inline-block h-1.5 w-1.5 rounded-full bg-yellow-300" aria-hidden="true" />
                  BOOT
                </span>
              ) : (
                <span className="shrink-0 text-[9px] tracking-[0.3em] text-yellow-200/60">
                  {firstRun ? 'LIVE' : '4 BARS'}
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={onOpenDemoInEditor}
              className="welcome-cta-secondary flex items-center justify-between rounded-sm border border-zinc-800 bg-zinc-900/40 px-4 py-2.5 text-left font-display text-[11px] uppercase tracking-[0.2em] text-zinc-200 transition-colors hover:border-yellow-300/40 hover:text-yellow-200"
            >
              <span>Open demo in editor</span>
              <span className="text-zinc-500 group-hover:text-yellow-200" aria-hidden="true">→</span>
            </button>

            <button
              type="button"
              onClick={onStartBlank}
              className="welcome-cta-secondary flex items-center justify-between rounded-sm border border-zinc-800 bg-zinc-900/40 px-4 py-2.5 text-left font-display text-[11px] uppercase tracking-[0.2em] text-zinc-200 transition-colors hover:border-yellow-300/40 hover:text-yellow-200"
            >
              <span>Start a blank song</span>
              <span className="text-zinc-500" aria-hidden="true">→</span>
            </button>
          </div>
        </section>

        {/* Ambient rhythm grid. Three rows × 16 steps. Each lit step
            pulses on a CSS keyframe; tempo doubles when demoPlaying. */}
        <section
          className="welcome-reveal flex flex-col gap-2"
          style={{ '--i': 4 } as React.CSSProperties}
        >
          <div className="flex items-center justify-between font-display text-[9px] uppercase tracking-[0.3em] text-zinc-500">
            <span>Pattern · 16 steps · 4/4</span>
            <span className="flex items-center gap-1" aria-hidden="true">
              <span className="welcome-meter-tick">|</span>
              <span className="welcome-meter-tick" style={{ animationDelay: '0.2s' }}>|</span>
              <span className="welcome-meter-tick" style={{ animationDelay: '0.4s' }}>|</span>
              <span className="welcome-meter-tick" style={{ animationDelay: '0.6s' }}>|</span>
            </span>
          </div>

          <div className="welcome-grid relative rounded-sm border border-zinc-800 bg-zinc-900/40 p-3">
            {/* Sweep playhead — a vertical phosphor line that travels
                left-to-right while playing. Pure CSS, restarts via the
                `data-playing` attribute on the parent. */}
            <div className="welcome-grid-playhead pointer-events-none absolute inset-y-0 w-[1px] bg-yellow-300/70" />

            <div className="grid gap-1.5">
              {ROWS.map((row) => (
                <div key={row.id} className="flex items-center gap-3">
                  <span className="w-8 shrink-0 font-display text-[9px] uppercase tracking-[0.18em] text-zinc-500">
                    {row.label}
                  </span>
                  <div className="grid flex-1 grid-cols-[repeat(16,minmax(0,1fr))] gap-[3px]">
                    {Array.from({ length: STEP_COUNT }, (_, i) => {
                      const hit = row.hits.includes(i);
                      const downbeat = i % 4 === 0;
                      return (
                        <span
                          key={i}
                          className={cellClass(hit, downbeat)}
                          style={
                            hit
                              ? ({ ['--step' as string]: String(i) } as React.CSSProperties)
                              : undefined
                          }
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Recent — most-recently-edited songs. Hidden when empty so a
            cold install (no edits yet) doesn't show a placeholder. */}
        {recentSongs.length > 0 && (
          <HomeStrip
            title="Recent"
            blurb="Pick up where you left off."
            cssOrder={5}
          >
            {recentSongs.map((song) => (
              <SongCard
                key={song.id}
                song={song}
                onOpen={() => onOpenSong(song.id)}
                onToggleFavourite={() => onToggleFavourite(song.id)}
              />
            ))}
          </HomeStrip>
        )}

        {/* Favourites — pinned via the star toggle. Hidden when none. */}
        {favouriteSongs.length > 0 && (
          <HomeStrip
            title="Favourites"
            blurb="Songs you've starred."
            cssOrder={6}
          >
            {favouriteSongs.map((song) => (
              <SongCard
                key={song.id}
                song={song}
                onOpen={() => onOpenSong(song.id)}
                onToggleFavourite={() => onToggleFavourite(song.id)}
              />
            ))}
          </HomeStrip>
        )}

        {/* Lessons — curated entry-points (one per module). Each card
            opens Learn at that lesson via the lesson handler. */}
        {lessonCards.length > 0 && (
          <HomeStrip
            title="Lessons"
            blurb="Tutorials, by module."
            cssOrder={7}
          >
            {lessonCards.map((card) => (
              <LessonCardView
                key={card.id}
                card={card}
                onOpen={() => onOpenLesson(card.id)}
              />
            ))}
          </HomeStrip>
        )}

        {/* Footer: keyboard shortcuts + community link. */}
        <footer
          className="welcome-reveal mt-auto flex flex-wrap items-center justify-between gap-4 border-t border-zinc-800/80 pt-5 text-[10px] uppercase tracking-[0.25em] text-zinc-500"
          style={{ '--i': 8 } as React.CSSProperties}
        >
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <ShortcutHint k="⌘K" label="commands" />
            <ShortcutHint k="⌘↵" label="play" />
            <ShortcutHint k="⌘S" label="save" />
          </div>
          <button
            type="button"
            onClick={onBrowseCommunity}
            className="text-zinc-400 transition-colors hover:text-yellow-200"
          >
            Browse what others have made →
          </button>
        </footer>
      </div>
    </div>
  );
}

function ShortcutHint({ k, label }: { k: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <kbd className="rounded-sm border border-zinc-700 bg-zinc-900 px-1.5 py-[1px] font-display text-[10px] tracking-normal text-zinc-300">
        {k}
      </kbd>
      <span>{label}</span>
    </span>
  );
}

function cellClass(hit: boolean, downbeat: boolean): string {
  const base =
    'h-3 rounded-[1px] transition-colors';
  if (hit) {
    // Lit cells get a phosphor pulse keyed off `--step` (positional
    // delay) so the lit row reads as motion across the bar.
    return `${base} welcome-cell-lit bg-yellow-300/30`;
  }
  if (downbeat) {
    return `${base} bg-zinc-700/50`;
  }
  return `${base} bg-zinc-800/60`;
}

function enginePhaseLabel(phase: PlayerPhase): string {
  switch (phase) {
    case 'loading':
      return 'ENGINE · WARMING';
    case 'ready':
      return 'ENGINE · READY';
    case 'playing':
      return 'ENGINE · PLAYING';
    case 'paused':
      return 'ENGINE · PAUSED';
    case 'error':
      return 'ENGINE · FAULT';
    default:
      return 'ENGINE · IDLE';
  }
}

// Highlight the demo snippet by hand — bringing in a syntax highlighter
// here would dwarf the welcome surface. Five colours: comment, string,
// keyword/function, identifier, number. Matches the existing CodeMirror
// theme without importing it. No-arg by design: operates only on the
// module-level DEMO_CODE_SNIPPET so the hand-rolled tokenizer can never
// receive untrusted input.
function renderDemoSnippet(): React.ReactNode {
  const src = DEMO_CODE_SNIPPET;
  // Simple tokenizer: match strings, comments, then word-likes.
  // We emit spans with phosphor-themed colours. The regex order matters
  // — comments first so a // inside a string is impossible (Strudel
  // patterns don't include // anyway).
  const tokens: { kind: string; text: string }[] = [];
  let i = 0;
  while (i < src.length) {
    const rest = src.slice(i);
    let m: RegExpMatchArray | null;
    if ((m = rest.match(/^\/\/[^\n]*/))) {
      tokens.push({ kind: 'comment', text: m[0] });
      i += m[0].length;
    } else if ((m = rest.match(/^"[^"]*"/))) {
      tokens.push({ kind: 'string', text: m[0] });
      i += m[0].length;
    } else if ((m = rest.match(/^[a-zA-Z_$][a-zA-Z0-9_$]*/))) {
      const next = src[i + m[0].length];
      const kind = next === '(' ? 'func' : 'ident';
      tokens.push({ kind, text: m[0] });
      i += m[0].length;
    } else if ((m = rest.match(/^\d+(\.\d+)?/))) {
      tokens.push({ kind: 'num', text: m[0] });
      i += m[0].length;
    } else {
      tokens.push({ kind: 'punct', text: src[i]! });
      i += 1;
    }
  }
  return tokens.map((t, idx) => {
    const cls = TOKEN_CLASS[t.kind] ?? '';
    return (
      <span key={idx} className={cls}>
        {t.text}
      </span>
    );
  });
}

const TOKEN_CLASS: Record<string, string> = {
  comment: 'text-zinc-500 italic',
  string: 'text-emerald-300/80',
  func: 'text-yellow-200',
  ident: 'text-zinc-300',
  num: 'text-orange-300/80',
  punct: 'text-zinc-500',
};

// ─── Home strips ────────────────────────────────────────────────────

// Generic horizontal strip used by Recent / Favourites / Lessons. Lays
// the cards out in a responsive grid (auto-fill at ~220px) so the strip
// stays readable from mobile up to wide desktop without horizontal
// scroll. The eyebrow + blurb pair mirrors the operator-console voice
// the rest of the page uses.
function HomeStrip({
  title,
  blurb,
  cssOrder,
  children,
}: {
  title: string;
  blurb: string;
  cssOrder: number;
  children: React.ReactNode;
}) {
  return (
    <section
      className="welcome-reveal flex flex-col gap-2"
      style={{ '--i': cssOrder } as React.CSSProperties}
    >
      <div className="flex items-baseline justify-between font-display text-[9px] uppercase tracking-[0.3em] text-zinc-500">
        <span>{title}</span>
        <span className="text-zinc-600 normal-case tracking-[0.18em] text-[10px]">
          {blurb}
        </span>
      </div>
      <div className="grid gap-2 grid-cols-[repeat(auto-fill,minmax(200px,1fr))]">
        {children}
      </div>
    </section>
  );
}

function SongCard({
  song,
  onOpen,
  onToggleFavourite,
}: {
  song: Song;
  onOpen: () => void;
  onToggleFavourite: () => void;
}) {
  const isFav = !!song.favourite;
  const stamp = formatRelativeTimestamp(song.modifiedAt);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group relative flex flex-col gap-1 rounded-sm border border-zinc-800 bg-zinc-900/40 px-3 py-2.5 text-left transition-colors hover:border-yellow-300/40 hover:bg-zinc-900/70"
    >
      <span
        // Star sits in the corner; clicking it toggles favourite and
        // doesn't bubble to the card open-action.
        role="button"
        tabIndex={0}
        aria-label={(isFav ? 'Unfavourite ' : 'Favourite ') + (song.title || 'untitled')}
        aria-pressed={isFav}
        onClick={(e) => {
          e.stopPropagation();
          onToggleFavourite();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.stopPropagation();
            e.preventDefault();
            onToggleFavourite();
          }
        }}
        className={
          'absolute right-2 top-2 cursor-pointer text-[12px] leading-none transition-colors ' +
          (isFav
            ? 'text-yellow-300 hover:text-yellow-200'
            : 'text-zinc-600 opacity-0 group-hover:opacity-100 hover:text-yellow-300')
        }
      >
        {isFav ? '★' : '☆'}
      </span>
      <span className="font-body text-[12.5px] text-zinc-100 truncate pr-5">
        {song.title || 'Untitled'}
      </span>
      <span className="font-display text-[9px] tracking-[0.18em] uppercase text-zinc-500">
        {stamp}
      </span>
    </button>
  );
}

function LessonCardView({
  card,
  onOpen,
}: {
  card: HomeLessonCard;
  onOpen: () => void;
}) {
  const dot = ACCENT_DOT[card.accent];
  const ink = ACCENT_INK[card.accent];
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group relative flex flex-col gap-1.5 rounded-sm border border-zinc-800 bg-zinc-900/40 px-3 py-2.5 text-left transition-colors hover:border-yellow-300/40 hover:bg-zinc-900/70"
    >
      <span className="flex items-center gap-1.5 font-display text-[9px] tracking-[0.32em] uppercase">
        <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${dot}`} />
        <span className={ink}>{card.moduleTitle}</span>
      </span>
      <span className="font-body text-[12.5px] text-zinc-100 leading-snug">
        {card.title}
      </span>
      <span className="font-display text-[9px] tracking-[0.18em] uppercase text-zinc-500">
        ~{card.estMinutes} min
      </span>
    </button>
  );
}

// Voice-LED tokens, locally defined to keep this surface independent
// of the learn module's accent table. Same colour values as
// ACCENT_TOKENS in src/learn/accent.ts so cards read identically here
// and inside the Learn modal.
const ACCENT_DOT: Record<HomeLessonCard['accent'], string> = {
  yellow: 'bg-yellow-300 shadow-[0_0_8px_rgba(253,224,71,0.5)]',
  emerald: 'bg-emerald-300 shadow-[0_0_8px_rgba(110,231,183,0.5)]',
  cyan: 'bg-cyan-300 shadow-[0_0_8px_rgba(103,232,249,0.5)]',
  fuchsia: 'bg-fuchsia-300 shadow-[0_0_8px_rgba(240,171,252,0.5)]',
};

const ACCENT_INK: Record<HomeLessonCard['accent'], string> = {
  yellow: 'text-yellow-300',
  emerald: 'text-emerald-300',
  cyan: 'text-cyan-300',
  fuchsia: 'text-fuchsia-300',
};

// Compact "12m ago" style stamp. Falls back to "—" when the song
// predates the modifiedAt field and hasn't been touched since.
function formatRelativeTimestamp(at: number | undefined): string {
  if (typeof at !== 'number' || at <= 0) return '—';
  const delta = Date.now() - at;
  if (delta < 0) return 'just now';
  const sec = Math.floor(delta / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  const wk = Math.floor(day / 7);
  if (wk < 5) return `${wk}w ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(day / 365)}y ago`;
}
